import { inject, Injectable, isDevMode } from '@angular/core';
import { ENVIRONMENT } from '@marmon/frontend/common';
import * as Sentry from '@sentry/angular';
import {
	formatConsolePrefix,
	LoggerReportOptions,
	toCaptureContext,
	toError,
} from '../utils/sentry.utils';

@Injectable({ providedIn: 'root' })
export class LoggerService {
	private readonly environment = inject(ENVIRONMENT, { optional: true });

	private get sentryEnabled(): boolean {
		return !!this.environment?.sentry;
	}

	/**
	 * Logs a warning to the console. Does not report to Sentry.
	 */
	warn(message: string, options?: LoggerReportOptions): void {
		console.warn(this.formatMessage(message, options), options?.extra);
	}

	/**
	 * Logs an error to the console. Does not report to Sentry.
	 */
	error(message: string, options?: LoggerReportOptions): void {
		console.error(this.formatMessage(message, options), options?.extra);
	}

	/**
	 * Debug logging for local development only.
	 */
	debug(message: string, options?: LoggerReportOptions): void {
		if (!isDevMode()) return;
		console.debug(this.formatMessage(message, options), options?.extra);
	}

	/**
	 * Reports an exception to Sentry when enabled, and always logs to the console.
	 *
	 * @returns Sentry event id when captured, otherwise undefined.
	 */
	captureException(
		error: string | Error,
		options?: LoggerReportOptions,
	): string | undefined {
		const err = toError(error);
		console.warn(this.formatMessage(err.message, options), options?.extra);

		if (!this.sentryEnabled) return undefined;

		return Sentry.captureException(err, toCaptureContext(options));
	}

	/**
	 * Reports a message to Sentry when enabled.
	 *
	 * @returns Sentry event id when captured, otherwise undefined.
	 */
	captureMessage(
		message: string,
		options?: LoggerReportOptions,
	): string | undefined {
		if (!this.sentryEnabled) {
			this.warn(message, options);
			return undefined;
		}

		return Sentry.captureMessage(
			message,
			toCaptureContext({ level: 'warning', ...options }),
		);
	}

	private formatMessage(
		message: string,
		options?: LoggerReportOptions,
	): string {
		return `${formatConsolePrefix(options?.source)} ${message}`;
	}
}
